// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@keller/App/Store/Reducers";
import systemColors from "@keller/XDL/PreStyle/colors";
import { layoutContainer } from "@keller/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

const enum fields {
    backgroundColor = "backgroundColor",
    fontColor = "fontColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
        [fields.fontColor]: string;
    };
}

type Props = OwnProps;

export interface MaintenanceMessageStyles {
    container?: InjectableCss;
    message?: TypographyPresentationProps;
}

export const maintenanceMessageStyles: MaintenanceMessageStyles = {
    container: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: 8px;

            ${({ backgroundColor }: any) => css`
                background-color: ${backgroundColor || "#750C0C"};
            `}
        `,
    },
    message: {
        size: 14,
        weight: "normal",
        css: css`
            line-height: 22px;
            ${layoutContainer}
            &,
            & > ${TypographyStyle} {
                margin-bottom: 0;
            }
        `,
    },
};

const styles = maintenanceMessageStyles;
const MaintenanceMessage: React.FC<Props> = ({ fields: { backgroundColor, fontColor } }) => {
    const isMaintenanceDown = useAppSelector(state => (state as CustomAppState).xMaintenance.data?.isDown);

    if (!isMaintenanceDown) {
        return null;
    }

    return (
        <StyledWrapper
            {...styles.container}
            {...({ backgroundColor } as any)}
            data-test-selector="maintenance_message_container"
        >
            <Typography
                {...styles.message}
                color={fontColor || "common.white"}
                data-test-selector="maintenance_message"
            >
                {siteMessage("Keller_Maintenance_Message")}
            </Typography>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: MaintenanceMessage,
    definition: {
        group: "Header",
        displayName: "Maintenance Message",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.fontColor,
                displayName: "Font Color",
                editorTemplate: "ColorPickerField",
                defaultValue: (systemColors as any).common.white,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
